import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getTemplatesList } from "../../../api/login.service";
import { getLeadRecovery, saveLeadRecovery } from "../../../api/leads.service";
import { businessId } from "../inbox/businessSetup";

type Unit = "minutes" | "hours" | "days";
type FollowChannel = "whatsapp" | "instagram" | "email" | "call";

type FollowStep = {
  id: string;
  amount: string;
  unit: Unit;
  templateId: string;
  templateName: string;
  message: string;
};

type Answers = {
  reachAmount: string;
  reachUnit: Unit;
  followChannel: FollowChannel;
  followUps: string;
  totalTouches: string;
  dormantPercent: string;
  followSteps: FollowStep[];
};

function newStep(amount = "", unit: Unit = "hours"): FollowStep {
  return {
    id: Math.random().toString(36).slice(2, 10),
    amount,
    unit,
    templateId: "",
    templateName: "",
    message: "",
  };
}

const CHANNELS: Array<{ id: FollowChannel; label: string }> = [
  { id: "whatsapp", label: "WhatsApp" },
  { id: "instagram", label: "Instagram" },
  { id: "email", label: "Email" },
  { id: "call", label: "Phone call" },
];

const STORAGE_KEY = "fd24.leadRecovery.v1";
const BRAND = "#2563eb";

const MAX_FOLLOW_UPS = 12;

function followStepAt(index: number): { amount: string; unit: Unit; label: string } {
  if (index === 0) return { amount: "6", unit: "hours", label: "At least 6 hours" };
  if (index === 1) return { amount: "24", unit: "hours", label: "At least 24 hours" };
  const days = 3 * (index - 1);
  return { amount: String(days), unit: "days", label: `At least ${days} days` };
}

function startingSteps(): FollowStep[] {
  return [0, 1, 2, 3].map((index) => {
    const step = followStepAt(index);
    return newStep(step.amount, step.unit);
  });
}

const EMPTY: Answers = {
  reachAmount: "",
  reachUnit: "hours",
  followChannel: "whatsapp",
  followUps: "",
  totalTouches: "",
  dormantPercent: "",
  followSteps: [],
};

function stepsFrom(raw: unknown): FollowStep[] {
  if (!Array.isArray(raw)) return startingSteps();
  const steps = raw.slice(0, MAX_FOLLOW_UPS).map((item) => {
    const row = item as Partial<FollowStep>;
    const unit = row.unit === "minutes" || row.unit === "days" ? row.unit : "hours";
    return {
      ...newStep(String(row.amount || ""), unit),
      templateId: String(row.templateId || ""),
      templateName: String(row.templateName || ""),
      message: String(row.message || ""),
    };
  }).filter((step) => step.amount);
  const signature = steps.map((step) => `${step.amount}${step.unit}`).join(",");
  if (steps.length === 0 || signature === "5minutes,1hours,2hours,3hours,8hours") return startingSteps();
  return steps;
}

function loadAnswers(): Answers {
  try {
    const all = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
    const saved = all[businessId()] || {};
    return {
      ...EMPTY,
      ...saved,
      followSteps: stepsFrom(saved.followSteps),
    };
  } catch {
    return { ...EMPTY, followSteps: startingSteps() };
  }
}

function saveAnswers(answers: Answers) {
  const all = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  all[businessId()] = answers;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
}

function positive(value: string): number | null {
  const amount = Number(value);
  if (!value.trim() || Number.isNaN(amount) || amount <= 0) return null;
  return amount;
}

function timesLabel(automated: number, yours: number): string {
  if (yours === automated) return "Same as FrontDesk";
  if (yours > automated) return "You do more";
  const times = Math.max(1, Math.round(automated / yours));
  return `${times}× more`;
}

type ApprovedTemplate = { id: string; name: string };

export default function LeadRecoveryPage() {
  const navigate = useNavigate();
  const [answers, setAnswers] = useState<Answers>(loadAnswers);
  const [templates, setTemplates] = useState<ApprovedTemplate[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  const set = (patch: Partial<Answers>) => setAnswers((current) => ({ ...current, ...patch }));
  const updateStep = (id: string, patch: Partial<FollowStep>) => {
    setAnswers((current) => ({
      ...current,
      followSteps: current.followSteps.map((step) => (step.id === id ? { ...step, ...patch } : step)),
    }));
  };

  useEffect(() => {
    const wabaId = sessionStorage.getItem("wabaId") || "";
    if (!wabaId) return;
    getTemplatesList(wabaId, 100)
      .then((data) => {
        const rows = Array.isArray(data?.data) ? data.data : [];
        setTemplates(
          rows
            .filter((item: { status?: string; id?: string; name?: string }) => item.status === "APPROVED" && item.id)
            .map((item: { id: string; name?: string }) => ({ id: item.id, name: item.name || item.id }))
        );
      })
      .catch(() => setTemplates([]));
  }, []);

  useEffect(() => {
    let cancelled = false;
    getLeadRecovery(businessId())
      .then((remote) => {
        if (cancelled || !remote) return;
        setAnswers({
          reachAmount: remote.reachAmount,
          reachUnit: remote.reachUnit,
          followChannel: remote.followChannel,
          followUps: remote.followUps,
          totalTouches: remote.totalTouches,
          dormantPercent: remote.dormantPercent,
          followSteps: stepsFrom(remote.followSteps),
        });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const channel = CHANNELS.find((item) => item.id === answers.followChannel) || CHANNELS[0];
  const isCall = answers.followChannel === "call";
  const extraQuestion = isCall
    ? "If a customer stops replying, how many extra times do you call?"
    : `If a customer stops replying, how many extra ${channel.label} messages do you send?`;
  const followUps = positive(answers.followUps);
  const shownFollowUps = followUps === null ? 0 : Math.min(MAX_FOLLOW_UPS, Math.floor(followUps));

  useEffect(() => {
    if (shownFollowUps <= answers.followSteps.length) return;
    setAnswers((current) => {
      if (shownFollowUps <= current.followSteps.length) return current;
      const followSteps = current.followSteps.slice();
      while (followSteps.length < shownFollowUps) {
        const fixed = followStepAt(followSteps.length);
        followSteps.push(newStep(fixed.amount, fixed.unit));
      }
      return { ...current, followSteps };
    });
  }, [shownFollowUps, answers.followSteps.length]);

  const goInbox = () => navigate("/events/conversations");

  return (
    <div className="min-h-screen bg-white px-6 py-16 text-gray-900">
      <div className="mx-auto w-full max-w-5xl">
        <h1 className="text-xl font-semibold text-gray-900">Lead recovery</h1>
        <p className="mb-6 mt-1 text-sm text-gray-500">Answer what you do today. The right column shows what FrontDesk does for the same customer.</p>

        <div className="overflow-hidden rounded-md border border-gray-200">
          <div className="grid grid-cols-[1fr_200px_150px] bg-gray-50 text-xs font-medium uppercase tracking-wide text-gray-400">
            <div />
            <div className="px-4 py-3 text-center">You</div>
            <div className="px-4 py-3 text-center">With FrontDesk</div>
          </div>

          <div className="grid grid-cols-[1fr_200px_150px] items-center border-t border-gray-100">
            <div className="px-6 py-5">
              <p className="text-sm font-medium text-gray-900">How do you follow up?</p>
              <p className="mt-1 text-xs leading-5 text-gray-400">Pick WhatsApp, Instagram, email, or a phone call. The next questions use that choice.</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {CHANNELS.map((item) => {
                  const selected = answers.followChannel === item.id;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => set({ followChannel: item.id })}
                      className={`rounded-full px-3 py-1 text-sm ${
                        selected ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {item.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <p className="px-3 text-center text-sm text-gray-700">{channel.label}</p>
            <div className="px-4 text-right">
              <p className="text-sm font-semibold" style={{ color: BRAND }}>WhatsApp and Instagram</p>
              <p className="text-xs text-gray-400">Message follow-up</p>
            </div>
          </div>

          <div className="grid grid-cols-[1fr_200px_150px] items-start border-t border-gray-100">
            <div className="px-6 py-5">
              <p className="text-sm font-medium text-gray-900">{extraQuestion}</p>
              <p className="mt-1 text-xs leading-5 text-gray-400">
                Only if the customer has not replied. If they reply, these stop and the regular flow continues.
                1 sends the 6-hour message. 2 adds 24 hours. After that they are 3 days apart: 3 days, 6 days, 9 days, and so on.
              </p>
              {(answers.followChannel === "whatsapp" || answers.followChannel === "instagram") && shownFollowUps > 0 ? (
                <div className="mt-4 space-y-2">
                  <p className="text-xs font-medium text-gray-500">
                    {answers.followChannel === "whatsapp" ? "The times are fixed. Pick a template for each one." : "The times are fixed. Write the message for each one."}
                  </p>
                  {Array.from({ length: shownFollowUps }, (_, index) => {
                    const fixed = followStepAt(index);
                    const step = answers.followSteps[index];
                    if (!step) return null;
                    return (
                    <div key={`${fixed.label}-${index}`} className="flex items-center gap-2">
                      <span className="w-36 shrink-0 text-xs text-gray-600">{fixed.label}</span>
                      {answers.followChannel === "whatsapp" ? (
                        <select
                          value={step.templateId}
                          onChange={(event) => {
                            const template = templates.find((item) => item.id === event.target.value);
                            updateStep(step.id, {
                              templateId: event.target.value,
                              templateName: template?.name || "",
                            });
                          }}
                          aria-label="Follow-up template"
                          className="min-w-0 flex-1 rounded border border-gray-200 bg-white px-2 py-1 text-sm outline-none focus:border-blue-600"
                        >
                          <option value="">Select a template</option>
                          {templates.map((template) => (
                            <option key={template.id} value={template.id}>{template.name}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          value={step.message}
                          onChange={(event) => updateStep(step.id, { message: event.target.value })}
                          placeholder="Instagram message"
                          aria-label="Instagram message"
                          className="min-w-0 flex-1 rounded border border-gray-200 px-2 py-1 text-sm outline-none focus:border-blue-600"
                        />
                      )}
                    </div>
                    );
                  })}
                  {answers.followChannel === "whatsapp" && templates.length === 0 ? (
                    <p className="text-xs text-gray-400">No approved templates yet.</p>
                  ) : null}
                  {shownFollowUps > 2 ? (
                    <p className="text-xs text-gray-400">After 24 hours the gaps are 3 days, 6 days, 9 days, and so on. Nothing is sent after the last one.</p>
                  ) : null}
                </div>
              ) : null}
            </div>
            <div className="flex items-center justify-center gap-2 px-3 py-5">
              <input
                type="number"
                min={0}
                value={answers.followUps}
                onChange={(event) => set({ followUps: event.target.value })}
                aria-label="Extra messages after they stop replying"
                className="w-20 rounded border border-gray-200 px-2 py-1 text-sm outline-none focus:border-blue-600"
              />
              <span className="text-sm text-gray-400">{isCall ? "calls" : "times"}</span>
            </div>
            <div className="px-4 py-5 text-right">
              {followUps === null ? null : (
                <>
                  <p className="text-sm font-semibold" style={{ color: BRAND }}>5 times</p>
                  <p className="text-xs text-gray-400">{timesLabel(5, followUps)}</p>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="mt-8 flex items-center justify-between border-t border-gray-100 pt-6">
          <button type="button" onClick={goInbox} className="text-sm text-gray-500 hover:text-gray-800">
            Skip
          </button>
          <div className="text-right">
            {saveError ? <p className="mb-2 text-xs text-red-600">{saveError}</p> : null}
            <button
              type="button"
              disabled={saving}
              onClick={async () => {
                setSaving(true);
                setSaveError("");
                saveAnswers(answers);
                try {
                  await saveLeadRecovery({
                    businessId: businessId(),
                    reachAmount: answers.reachAmount,
                    reachUnit: answers.reachUnit,
                    followChannel: answers.followChannel,
                    followUps: answers.followUps,
                    totalTouches: answers.totalTouches,
                    dormantPercent: answers.dormantPercent,
                    onlyIfNoReply: true,
                    followSteps: Array.from({ length: shownFollowUps }, (_, index) => {
                      const fixed = followStepAt(index);
                      const step = answers.followSteps[index];
                      return {
                        amount: fixed.amount,
                        unit: fixed.unit,
                        templateId: step?.templateId || "",
                        templateName: step?.templateName || "",
                        message: step?.message.trim() || "",
                      };
                    }),
                  });
                  goInbox();
                } catch {
                  setSaveError("Could not save to the server. Your answers are still on this browser.");
                  setSaving(false);
                }
              }}
              className="rounded-lg bg-blue-600 px-6 py-2.5 text-sm font-medium text-white disabled:opacity-60"
            >
              {saving ? "Saving..." : "Continue"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

